/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import Link from "next/link";
import {
  ArrowRight,
  Building2,
  Droplets,
  Home,
  Leaf,
  PlugZap,
  Ruler,
  Thermometer,
  Wrench,
  CheckCircle,
  Zap,
  Shield,
} from "lucide-react";
import MainNavigation from "@/components/layout/main-navigation";
import { Footer } from "@/components/landing/footer";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { EMAILS } from "@/lib/constants/master";
import { FadeIn } from "@/components/landing/primitives/fade-in";

const TRADES = [
  {
    name: "Roofing",
    href: "/solutions/roofing",
    pain: "Square counts, material waste, labor hours — priced from memory.",
    icon: Wrench,
  },
  {
    name: "Electrical",
    href: "/solutions/electrical",
    pain: "Panel schedules, wire runs, device counts — scattered across spreadsheets.",
    icon: PlugZap,
  },
  {
    name: "Plumbing",
    href: "/solutions/plumbing",
    pain: "Fixture counts, pipe sizing, fitting lists — no system ties them together.",
    icon: Droplets,
  },
  {
    name: "HVAC",
    href: "/solutions/hvac",
    pain: "Equipment specs, duct quantities, refrigerant lines — priced from last year's numbers.",
    icon: Thermometer,
  },
  {
    name: "Landscaping",
    href: "/solutions/landscaping",
    pain: "Plant lists, hardscape quantities, seasonal pricing — three suppliers, nothing matches.",
    icon: Leaf,
  },
  {
    name: "Concrete",
    href: "/solutions/concrete",
    pain: "Yardage calcs, rebar quantities, pour schedules — estimated on a notepad.",
    icon: Ruler,
  },
  {
    name: "General Contracting",
    href: "/solutions/general-contracting",
    pain: "Juggling sub bids across 10 trades. One missed scope item kills the margin.",
    icon: Building2,
  },
  {
    name: "Residential Construction",
    href: "/solutions/residential-construction",
    pain: "Whole-house estimates that take a full week. Every trade priced separately.",
    icon: Home,
  },
];

const VALUE_PROPS = [
  {
    icon: Zap,
    title: "Pre-loaded for your trade",
    description:
      "Pick your trade. Costs, categories, and labor rates are already there. Import your own pricing or start with ours.",
  },
  {
    icon: Shield,
    title: "One connected flow",
    description:
      "Scope to takeoff to estimate to proposal to project to invoice. No re-entry. No switching apps.",
  },
  {
    icon: CheckCircle,
    title: "AI that knows your numbers",
    description:
      "Ask Hank about your costs, your margins, your project data. Get real answers, not generic advice.",
  },
];

export function SolutionsPageClient() {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary to-primary/90 grain-texture min-h-[60vh] flex items-center">
        <div
          className="blueprint-grid-gold absolute inset-0 opacity-10"
          aria-hidden="true"
        />
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20 py-8 md:py-16 lg:py-20">
          <div className="max-w-3xl">
            <FadeIn
              as="span"
              className="inline-block mb-6 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold uppercase tracking-widest text-white/70"
            >
              Solutions by Trade
            </FadeIn>
            <FadeIn
              as="h1"
              className="text-[clamp(36px,6vw,64px)] leading-[1.05] font-bold font-display text-white mb-6 tracking-tight"
              delay={100}
            >
              Your trade. Your workflow.{" "}
              <span className="text-secondary">Your numbers.</span>
            </FadeIn>
            <FadeIn
              as="p"
              className="text-lg text-white/50 leading-relaxed max-w-2xl"
              delay={200}
            >
              Every trade estimates differently. Contractor Co-Pilot comes
              pre-loaded with the materials, labor rates, and cost categories
              for your trade — so you&apos;re working from day one, not building
              a system from scratch.
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Trade Grid */}
      <section className="py-16 md:py-24" aria-label="Choose your trade">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <header className="text-center mb-12">
            <h2 className="text-[clamp(28px,4vw,44px)] leading-[1.1] font-bold font-display text-foreground mb-4">
              Find your trade
            </h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              Each page shows exactly how CC handles your type of work.
            </p>
          </header>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {TRADES.map(({ name, href, pain, icon: Icon }, index) => (
              <FadeIn key={href} delay={index * 100}>
                <Link
                  href={href}
                  className="card-premium group relative flex flex-col rounded-xl border border-border bg-background p-5 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-secondary/40 h-full"
                >
                  <div
                    className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center mb-4"
                    aria-hidden="true"
                  >
                    <Icon className="h-5 w-5 text-secondary" />
                  </div>
                  <h3 className="text-base font-bold font-display text-foreground mb-2">
                    {name}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                    {pain}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground group-hover:text-foreground transition-colors">
                    See how
                    <ArrowRight
                      className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16 md:py-24 bg-muted/30" aria-label="Why CC">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <header className="text-center mb-12">
            <span className="section-badge mb-4">Why CC</span>
            <h2 className="text-[clamp(28px,4vw,44px)] leading-[1.1] font-bold font-display text-foreground mb-4">
              Why contractors switch
            </h2>
          </header>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {VALUE_PROPS.map(({ icon: Icon, title, description }, index) => (
              <FadeIn key={title} className="text-center" delay={index * 100}>
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                  <Icon
                    className="h-6 w-6 text-foreground"
                    aria-hidden="true"
                  />
                </div>
                <h3 className="text-base font-bold font-display text-foreground mb-2">
                  {title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {description}
                </p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <MarketingCTASection
        title="Start with your trade"
        description="7-day free trial. Pre-loaded cost data. Your first estimate in minutes."
        primaryCTA={{ text: "Start Your Free Trial", href: "/auth/sign-up" }}
        secondaryCTA={{
          text: "Contact us",
          href: `mailto:${EMAILS.CONTACT}`,
        }}
        trustIndicators={[
          "7-day free trial",
          "No long-term contracts",
          "Cancel anytime",
        ]}
      />

      <Footer />
    </div>
  );
}
